/**
 * Scramble Engine
 *
 * Character-by-character text scramble morphing.
 * Used for the codebreak easter egg and the Go Deeper intro.
 */

const SCRAMBLE_CHARS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ?!—'…,.";

/** Fisher-Yates shuffle (returns new array) */
function shuffle<T>(arr: T[]): T[] {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = a[i];
    if (tmp === undefined || a[j] === undefined) continue;
    a[i] = a[j];
    a[j] = tmp;
  }
  return a;
}

/* ── Types ───────────────────────────────────────────────────── */

interface CharState {
  current: string;
  target: string;
  resolved: boolean;
  spacer: boolean;
  span: HTMLSpanElement | null;
}

export interface ScrambleOptions {
  /** Total scramble window in ms (default 1200) */
  scrambleDuration?: number;
  /** Pure chaos before resolving begins in ms (default 400) */
  chaosHold?: number;
  /** Positions resolve over this period in ms (default 800) */
  resolveWindow?: number;
}

/* ── Core scramble ───────────────────────────────────────────── */

/**
 * Character-by-character scramble morph between two strings.
 *
 * Pads the shorter string so both have equal length (using spaces).
 * Each character position goes through a random scramble before
 * resolving to its target. Positions resolve in shuffled order.
 */
export function scrambleMorph(
  el: HTMLElement,
  from: string,
  to: string,
  timers: ReturnType<typeof setTimeout>[],
  onComplete?: () => void,
  options?: ScrambleOptions
): void {
  // Pad to equal length
  const maxLen = Math.max(from.length, to.length);
  const src = from.padEnd(maxLen);
  const dst = to.padEnd(maxLen);

  // Build character states — mark space positions as spacers so word breaks survive chaos
  const chars: CharState[] = src.split('').map((c, i) => {
    const target = dst.charAt(i);
    const isSpace = target === ' ';
    return {
      current: isSpace ? ' ' : c,
      target,
      resolved: isSpace,
      spacer: isSpace,
      span: null,
    };
  });

  // Build persistent DOM spans (no innerHTML rebuilds)
  buildCharSpans(el, chars);

  // Scramble settings
  const SCRAMBLE_DURATION = options?.scrambleDuration ?? 1200;
  const TICK_INTERVAL = 50;
  const RESOLVE_WINDOW = options?.resolveWindow ?? 800;
  const CHAOS_HOLD = options?.chaosHold ?? 400;
  const FADE_MS = 150;

  // Order in which positions resolve (shuffled)
  const resolveOrder = shuffle([...new Array(maxLen).keys()]);
  const resolveGap = maxLen > 1 ? RESOLVE_WINDOW / (maxLen - 1) : 0;

  // Tick: randomize unresolved positions by updating textContent directly
  const tickTimer = setInterval(() => {
    for (const c of chars) {
      if (!c.resolved && !c.spacer && c.span) {
        c.current = SCRAMBLE_CHARS.charAt(Math.floor(Math.random() * SCRAMBLE_CHARS.length));
        c.span.textContent = c.current;
      }
    }
  }, TICK_INTERVAL);
  timers.push(tickTimer as unknown as ReturnType<typeof setTimeout>);

  // Schedule resolves with per-character crossfade
  resolveOrder.forEach((charIdx, step) => {
    const delay = CHAOS_HOLD + step * resolveGap;
    const t = setTimeout(() => {
      const c = chars[charIdx];
      if (!c?.span) return;

      // Phase 1: fade out current scramble char
      c.span.classList.add('cb-fading');

      // Phase 2: after fade-out, swap text and fade back in
      const fadeTimer = setTimeout(() => {
        if (!c.span) return;
        c.resolved = true;
        c.current = c.target;
        c.span.textContent = c.current;
        c.span.classList.remove('cb-fading', 'cb-scrambling');
        c.span.classList.add('cb-resolved');
      }, FADE_MS);
      timers.push(fadeTimer);
    }, delay);
    timers.push(t);
  });

  // Final cleanup
  const cleanupTimer = setTimeout(() => {
    clearInterval(tickTimer);
    for (const c of chars) {
      c.resolved = true;
      c.current = c.target;
      if (c.span) {
        c.span.textContent = c.current;
        c.span.classList.remove('cb-fading', 'cb-scrambling');
        c.span.classList.add('cb-resolved');
      }
    }
    onComplete?.();
  }, SCRAMBLE_DURATION);
  timers.push(cleanupTimer);
}

/* ── DOM helpers ─────────────────────────────────────────────── */

/** Build persistent char spans once — store refs for direct DOM updates */
function buildCharSpans(el: HTMLElement, chars: CharState[]): void {
  const words: CharState[][] = [];
  let currentWord: CharState[] = [];
  for (const c of chars) {
    if (c.spacer) {
      if (currentWord.length > 0) {
        words.push(currentWord);
        currentWord = [];
      }
    } else {
      currentWord.push(c);
    }
  }
  if (currentWord.length > 0) words.push(currentWord);

  el.innerHTML = '';
  for (const word of words) {
    const wordSpan = document.createElement('span');
    wordSpan.className = 'cb-word';
    for (const c of word) {
      const charSpan = document.createElement('span');
      charSpan.className = c.resolved ? 'cb-char cb-resolved' : 'cb-char cb-scrambling';
      charSpan.style.setProperty('--cb-opacity', (0.6 + Math.random() * 0.5).toFixed(2));
      charSpan.textContent = c.current;
      c.span = charSpan;
      wordSpan.appendChild(charSpan);
    }
    el.appendChild(wordSpan);
  }
}

/** Render characters with word grouping for initial static display */
export function renderChars(el: HTMLElement, chars: { current: string; resolved: boolean; spacer?: boolean }[]): void {
  const words: { current: string; resolved: boolean }[][] = [];
  let currentWord: { current: string; resolved: boolean }[] = [];
  for (const c of chars) {
    if (c.current === ' ') {
      if (currentWord.length > 0) {
        words.push(currentWord);
        currentWord = [];
      }
    } else {
      currentWord.push(c);
    }
  }
  if (currentWord.length > 0) words.push(currentWord);

  el.innerHTML = words
    .map((word) => {
      const inner = word
        .map(({ current, resolved }) => {
          const cls = resolved ? 'cb-char cb-resolved' : 'cb-char cb-scrambling';
          const opacity = (0.6 + Math.random() * 0.5).toFixed(2);
          return `<span class="${cls}" style="--cb-opacity:${opacity}">${current}</span>`;
        })
        .join('');
      return `<span class="cb-word">${inner}</span>`;
    })
    .join('');
}
